/**
 * In-memory transfer store for moving document text between pages
 * (e.g., from /cautare auto-routing into /analiza-document).
 *
 * Security & Privacy:
 * - In-memory only (never saved to localStorage, sessionStorage, or URL query parameters).
 * - Single-use (deleted immediately upon consumption).
 * - Auto-expires after TTL (5 minutes).
 */

export interface DocumentAnalysisTransfer {
  file?: File;
  text: string;
  source: 'search' | 'external';
  fileName?: string;
  ownerId?: string | number | null;
  createdAt: number;
}

const TRANSFER_TTL_MS = 5 * 60 * 1000; // 5 minutes
const transferStore = new Map<string, DocumentAnalysisTransfer>();

function cleanExpired(): void {
  const now = Date.now();
  for (const [id, item] of transferStore.entries()) {
    if (now - item.createdAt > TRANSFER_TTL_MS) {
      transferStore.delete(id);
    }
  }
}

export function clearAllDocumentAnalysisTransfers(): void {
  transferStore.clear();
}

export function createDocumentAnalysisTransfer(payload: {
  text: string;
  source?: 'search' | 'external';
  file?: File;
  fileName?: string;
  ownerId?: string | number | null;
}): string {
  cleanExpired();
  const id = `transfer-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  transferStore.set(id, {
    text: payload.text,
    file: payload.file,
    source: payload.source ?? 'search',
    fileName: payload.fileName,
    ownerId: payload.ownerId,
    createdAt: Date.now(),
  });
  return id;
}

export function consumeDocumentAnalysisTransfer(
  transferId: string | null | undefined,
  currentOwnerId?: string | number | null,
): DocumentAnalysisTransfer | null {
  if (!transferId) return null;
  cleanExpired();
  const found = transferStore.get(transferId);
  if (!found) return null;
  transferStore.delete(transferId);
  if (Date.now() - found.createdAt > TRANSFER_TTL_MS) {
    return null;
  }
  if (found.ownerId != null && (currentOwnerId == null || String(found.ownerId) !== String(currentOwnerId))) {
    return null;
  }
  return found;
}

const CONTRACT_TITLE_REGEX = /\b(?:CONTRACT|CONVEN[ȚT]IE|ACT\s+ADI[ȚT]IONAL|ACORD(?:\s+CONTRACTUAL)?|TERMENI\s+[ȘS]I\s+CONDI[ȚT]II)\b/i;
const ARTICLE_REGEX = /\b(?:Art(?:icolul)?\.?\s*\d+|Clauz[aă]\s*\d+|Cap(?:itolul)?\.?\s*[IVXLCDM\d]+)\b/gi;
const ROLE_REGEX = /\b(?:prestator|beneficiar|locator|locatar|vanzator|vânzător|cumparator|cumpărător|contractant|partile|părțile|achizitor|furnizor|angajator|salariat|mandant|mandatar|cedent|cesionar)\b/gi;
const CLAUSE_REGEX = /(?:obiectul contractului|durata contractului|pre[tț]ul|modalit[aă][tț]i de plat[aă]|obliga[tț]iile p[aă]r[tț]ilor|r[aă]spunderea contractual[aă]|for[tț][aă] major[aă]|reziliere|încetarea contractului|incetarea contractului|penalit[aă][tț]i de întârziere|penalitati de intarziere|pact comisoriu|legea aplicabil[aă]|litigii)/gi;

export function isContractualDocumentCandidate(text: string): boolean {
  if (!text) return false;
  const clean = text.trim();
  if (clean.length < 300) return false;
  const hasTitle = CONTRACT_TITLE_REGEX.test(clean.slice(0, 300));
  const articleMatches = clean.match(ARTICLE_REGEX) || [];
  const roleMatches = new Set((clean.match(ROLE_REGEX) || []).map(r => r.toLowerCase()));
  const clauseMatches = new Set((clean.match(CLAUSE_REGEX) || []).map(c => c.toLowerCase()));

  if (hasTitle && (articleMatches.length >= 1 || roleMatches.size >= 1 || clauseMatches.size >= 1)) {
    return true;
  }
  if (articleMatches.length >= 2 && (roleMatches.size >= 1 || clauseMatches.size >= 1)) {
    return true;
  }
  return false;
}


export type DocumentTransfer = DocumentAnalysisTransfer;
export const createDocumentTransfer = createDocumentAnalysisTransfer;
export const consumeDocumentTransfer = consumeDocumentAnalysisTransfer;
